import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MouseEventHandler, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';

import type { RootState } from '@verantos-dev/app-state-base';
import { AppHeader, Footer, Sidebar } from '@verantos-dev/shared-components';

import { useSectionName } from './useSectionName';
import { useSidebarItems } from './useSidebarItems';

export const AppPageLayout = () => {
  const theme = useTheme();

  const navigate = useNavigate();
  const appId = useSelector((state: RootState) => state.app.id);

  // get sidebar items for the current user
  const sectionName = useSectionName(appId);
  const sidebar = useSidebarItems();
  const sidebarItems = sidebar?.sidebarItems;

  const pathname = window.location.pathname;
  const disableSidePadding = pathname.toLowerCase().includes('cohortbuilder');

  const onLogoClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      e.preventDefault();
      navigate(`/${appId}`);
    },
    [appId, navigate]
  );

  return (
    <Box
      data-testid="AppWrapperV2"
      sx={{
        width: `100%`,
        height: `100%`,
        minWidth: '1366px',
        minHight: '768px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <AppHeader appSectionName={sectionName} onLogoClick={onLogoClick} />
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100% - 51px)', // the extra space for the app header
          width: '100%',
        }}
      >
        <Sidebar id="appSidebar" items={sidebarItems ?? []} selectedItem={''} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: 'calc(100% - 64px)',
            flexGrow: 1,
            overflowX: 'auto',
          }}
        >
          <Box
            data-testid="appContent"
            sx={{
              marginBottom: '80px',
              px: disableSidePadding ? 0 : `${theme.spacing(6)}`,
              width: '100%',
            }}
          >
            <Outlet />
          </Box>

          <Footer />
        </Box>
      </Box>
    </Box>
  );
};
